import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { Link, useNavigate } from "react-router-dom";

import Loading from "../../components/loading";
import moment from "moment";
import { toastError, toastSuccess } from "../../utils";
import PartyCardDetail from "../party/partyCardDetail";
import Pagination from "../../components/Pagination";
import { confirmDelete } from "../../components/confirmDelete";
import {
  deleteSaleOrder,
  getAllSaleOrders,
  getSingleSaleOrder,
} from "../../services/services";

const SaleOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isConvertedEdit, setIsConvertedEdit] = useState(false);

  const navigate = useNavigate();

  /* ===== FETCH ORDERS ===== */
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await getAllSaleOrders({
        page: currentPage,
        limit,
      });

      const data = res.data.response;

      setOrders(data.sales || []);
      setTotalPages(res.data.totalPages || 1);
    } catch {
      toastError("Failed to load orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, limit]);

  /* ===== DELETE ===== */
  const handleDelete = async (id, name) => {
    const confirm = await confirmDelete(name);
    if (!confirm) return;

    try {
      const res = await deleteSaleOrder(id);
      toastSuccess("Sale Order deleted!");
      await fetchOrders();
      setSelectedOrder(null);
    } catch {
      toastError("Delete failed");
    }
  };

  /* ===== SELECT ORDER (UNCHANGED LOGIC) ===== */
  const handleSelectOrder = async (order) => {
    try {
      setLoading(true);

      const res = await getSingleSaleOrder(order._id);
      const data = res.data.response;
      setIsConvertedEdit(data.isConverted);

      const customer = data.customerId || {};

      setSelectedOrder({
        _id: data._id,
        referenceId: data._id,
        referenceType: "sale-order",

        invoiceNumber:
          data.invoiceNumber || `SO-${data._id.slice(-6).toUpperCase()}`,

        date: data.saleDate || data.createdAt,

        totalAmount: data.totalAmount || 0,
        dueAmount: data.dueAmount || 0,
        receivedAmount: data.receivedAmount || 0,
        paymentMode: data.paymentMode || "cash",

        party: {
          _id: customer._id || "",
          name: customer.name || "Walk-in Customer",
          mobile: customer.mobile || "",
          address: customer.address || "",
          credit_balance: customer.credit_balance || 0,
        },

        items: (data.items || []).map((item) => {
          const product = item.productId || {};

          const itemName =
            typeof item.productId === "object" ? product.name : item.itemName;

          return {
            itemName: itemName || "Unknown Item",
            quantity: item.sellingQty || item.quantity || 0,
            sellingPrice: item.sellingPrice || item.salePrice || 0,
            unit: product.unit || item.unit || "pcs",
            gstRate: item.gstRate || 0,
            taxType: item.taxType || "exclusive",
          };
        }),

        services: data.services || [],
        otherExpenses: data.otherExpenses || [],
        discountAmount: data.discountAmount || 0,
        discountType: data.discountType || "",
        discountValue: data.discountValue || 0,
        totalBeforeDiscount: data.totalBeforeDiscount || 0,
      });
    } catch (error) {
      console.error(error);
      toastError("Failed to load sale order details");
    } finally {
      setLoading(false);
    }
  };

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        // fontWeight: "600",
        // fontSize: "14px",
        textTransform: "uppercase",
      },
    },
  };
  /* ===== DATA TABLE COLUMNS ===== */
  const columns = useMemo(
    () => [
      {
        name: "S.No.",
        selector: (row) => row.invoiceNumber || "-",
        sortable: true,
      },
      {
        name: "Customer",
        selector: (row) => row.customerId?.name || "-",
      },
      {
        name: "Mobile",
        selector: (row) => row.customerId.mobile || "-",
      },
      {
        name: "Items",
        cell: (row) => {
          const firstItem =
            row.items?.[0]?.productId?.name ||
            row.items?.[0]?.itemName ||
            "Item";

          return (
            <span className="flex items-center">
              <span>{firstItem}</span>

              {row.items?.length > 1 && (
                <small className="text-muted ml-1">
                  +{row.items.length - 1} more
                </small>
              )}
            </span>
          );
        },
      },
      {
        name: "Total",
        cell: (row) => `₹ ${(row.totalAmount || 0).toFixed(2)}`,
      },
      {
        name: "Sold",
        cell: (row) => (
          <span
            className={`badge ${row.isConverted ? "bg-success" : "bg-danger"}`}
          >
            {row.isConverted ? "Yes" : "No"}
          </span>
        ),
      },
      {
        name: "Date",
        selector: (row) =>
          moment(row.saleDate || row.createdAt).format("DD MMM, YYYY"),
      },
      {
        name: "Action",
        center: true,
        width: "15rem",
        cell: (row) => (
          <div className="d-flex gap-2">
            <button
              className={`btn btn-light btn-sm shadow-sm text-primary border text-nowrap ${
                row.isConverted ? "disabled-btn" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/sales/create/${row._id}`, { state: "sale-order" });
              }}
              disabled={row.isConverted}
            >
              {row.isConverted ? "Sold" : "Convert to Sale"}
            </button>

            <button
              className="btn btn-light btn-sm shadow-sm text-danger border"
              onClick={(e) => {
                e.stopPropagation();
                const customer = row.customerId?.name || "Customer";
                const invoice = row.invoiceNumber || row._id.slice(-6);
                handleDelete(row._id, `${customer} (${invoice})`);
              }}
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        ),
      },
    ],
    [navigate],
  );

  return (
    <>
      <div id="main">
        <div className="d-flex justify-content-between mb-4 mt-2">
          <h2 className="custom-title mb-0">Manage Sale Orders</h2>
          <Link to="/sales/order">
            <button className="btn btn-primary btn-sm">
              Create Sale Order
            </button>
          </Link>
        </div>

        <div className="card">
          <div className="card-body p-0">
            <DataTable
              columns={columns}
              data={orders}
              progressPending={loading}
              customStyles={customStyles}
              progressComponent={<Loading />}
              noDataComponent={
                <div className="text-center p-5 text-muted">
                  <i className="bi bi-inbox display-1"></i>
                  <p className="mt-3">No sale orders found</p>
                </div>
              }
              highlightOnHover
              striped
              onRowClicked={handleSelectOrder}
            />
          </div>
        </div>
        {orders.length > 0 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            limit={limit}
            onPageChange={setCurrentPage}
            onLimitChange={setLimit}
          />
        )}

        {selectedOrder && (
          <>
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
              style={{ zIndex: 1049 }}
              onClick={() => setSelectedOrder(null)}
            />
            <PartyCardDetail
              ledgerEntry={selectedOrder}
              isConvertedEdit={isConvertedEdit}
              onClose={() => setSelectedOrder(null)}
              onDeleteSuccess={() => {
                setSelectedOrder(null);
                fetchOrders({ page: currentPage, limit });
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default headerLayoutWrapper(SaleOrderList);
